import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400 mb-2" }
const _hoisted_2 = {
  key: 0,
  class: "flex flex-col gap-y-2 mt-2"
}
const _hoisted_3 = {
  key: 1,
  class: "flex justify-end gap-x-2 mt-4 pt-3 border-t"
}

import { ref, computed, watch, nextTick } from 'vue';
import { ChevronDown } from 'lucide-vue-next';
import { subMonths, parseISO, startOfDay } from 'date-fns';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import DatePicker from '@/components/ui/DatePicker.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { splitPeriodRangeString, joinPeriodRangeString } from '@/util/period';

interface Props {
    presetMonths?: number[];
}

const CALENDAR_HEIGHT_ESTIMATE = 360;

// The calendar's own grid (week numbers + days) is a fixed ~408px, wider than the now
// content-sized popover - centering it under the field (below) can push it past the window edge
// when the popover sits near a viewport edge, so that default needs clamping back on-screen.
const CALENDAR_WIDTH_ESTIMATE = 408;


export default /*@__PURE__*/_defineComponent({
  __name: 'PeriodRangeDropdown',
  props: /*@__PURE__*/_mergeModels({
    presetMonths: { default: () => [3, 6, 9, 12] }
  }, {
    "value": { required: true },
    "valueModifiers": {},
  }),
  emits: ["update:value"],
  setup(__props: any) {

const props = __props;

const modelValue = _useModel<string>(__props, 'value');

const isOpen = ref(false);
const draftChoice = ref('custom');
const draftFrom = ref('');
const draftTo = ref('');

// Flatpickr's own viewport auto-flip is off (the house `static: true` wrapper convention hands
// positioning to CSS instead), so a field near the bottom of the viewport needs an explicit flip
// to 'above' or its calendar renders past the window edge.
const popoverBodyEl = ref<HTMLElement | null>(null);
const fromFieldEl = ref<HTMLElement | null>(null);
const toFieldEl = ref<HTMLElement | null>(null);

// FLIP: the card's width is content-driven (shrink-to-fit, see the class on PopoverContent above),
// so there's no fixed start/end value for CSS to transition between. Measure the width before the
// preset/Custom content swaps, let it swap, measure the new natural width, then animate explicitly
// between those two px values before releasing back to auto. The token guards against two toggles
// fired in quick succession: a stale transitionend must not clear a width a newer call just set.
let widthAnimationToken = 0;

async function animatePopoverWidth() {
    const el = popoverBodyEl.value;

    if (!el) {
        return;
    }

    const token = ++widthAnimationToken;
    const fromWidth = el.getBoundingClientRect().width;

    await nextTick();

    if (token !== widthAnimationToken) {
        return;
    }

    el.style.width = 'auto';
    const toWidth = el.getBoundingClientRect().width;

    if (Math.abs(toWidth - fromWidth) < 1) {
        el.style.width = '';
        return;
    }

    el.style.width = `${fromWidth}px`;
    void el.offsetWidth; // force reflow so the line below starts a transition instead of a jump
    el.style.width = `${toWidth}px`;

    await new Promise<void>((resolve) => el.addEventListener('transitionend', () => resolve(), { once: true }));

    if (token === widthAnimationToken) {
        el.style.width = '';
    }
}
const fromCalendarPosition = ref<'below' | 'above'>('below');
const toCalendarPosition = ref<'below' | 'above'>('below');

function positionForField(el: HTMLElement | null): 'below' | 'above' {
    if (!el) {
        return 'below';
    }

    const spaceBelow = window.innerHeight - el.getBoundingClientRect().bottom;

    return spaceBelow < CALENDAR_HEIGHT_ESTIMATE ? 'above' : 'below';
}

function alignCalendarHorizontally(fieldEl: HTMLElement | null) {
    const wrapper = fieldEl?.querySelector<HTMLElement>('.flatpickr-wrapper');

    if (!fieldEl || !wrapper) {
        return;
    }

    const fieldRect = fieldEl.getBoundingClientRect();
    const centeredLeft = fieldRect.left + fieldRect.width / 2 - CALENDAR_WIDTH_ESTIMATE / 2;
    const clampedLeft = Math.min(Math.max(centeredLeft, 8), window.innerWidth - CALENDAR_WIDTH_ESTIMATE - 8);

    wrapper.style.left = `${clampedLeft - fieldRect.left}px`;
    wrapper.style.transform = 'none';
}

async function updateCalendarPositions() {
    // The popover's own content mounts (and settles its floating-ui placement) a frame after the
    // `open`/`draftChoice` state that reveals it, so a plain `nextTick` alone can still measure a
    // pre-layout position. Waiting a paint past it gives the final, on-screen geometry.
    await nextTick();
    await new Promise(requestAnimationFrame);

    fromCalendarPosition.value = positionForField(fromFieldEl.value);
    toCalendarPosition.value = positionForField(toFieldEl.value);
    alignCalendarHorizontally(fromFieldEl.value);
    alignCalendarHorizontally(toFieldEl.value);
}

function monthsLabel(months: number) {
    return i18n.global.t('global.uiElements.periodRangeDropdown.monthsPresetLabel').replace('$NUMBER', `${months}`);
}

const presetOptions = computed(() => props.presetMonths.map((months) => ({ months, label: monthsLabel(months) })));

function matchingPresetMonths(from: string, to: string): number | null {
    const toDate = parseISO(to);
    const fromDate = startOfDay(parseISO(from)).getTime();

    return props.presetMonths.find((months) => startOfDay(subMonths(toDate, months)).getTime() === fromDate) ?? null;
}

function seedDraft() {
    const [from, to] = splitPeriodRangeString(modelValue.value);
    const preset = matchingPresetMonths(from, to);

    draftFrom.value = from;
    draftTo.value = to;
    draftChoice.value = preset !== null ? `preset-${preset}` : 'custom';
}

seedDraft();

watch(isOpen, (open) => {
    if (open) {
        seedDraft();
    }
});

// One watcher covering both the moment the popover opens into the custom state and the moment
// the user switches to it while already open — Vue batches the `draftChoice` assignment from
// `seedDraft()` together with the `isOpen` change that caused it, so this fires once per case.
// The width animation is awaited first: positioning a field against a card still mid-transition
// would measure a stale rect and leave the calendar unaligned once the width settles.
watch([isOpen, draftChoice], async ([open, choice]) => {
    if (!open) {
        return;
    }

    await animatePopoverWidth();

    if (choice === 'custom') {
        updateCalendarPositions();
    }
});

const triggerLabel = computed(() => {
    const [from, to] = splitPeriodRangeString(modelValue.value);
    const preset = matchingPresetMonths(from, to);

    return preset !== null ? monthsLabel(preset) : i18n.global.t('global.uiElements.periodRangeDropdown.customOptionLabel');
});

function onFieldChange(field: 'from' | 'to', newValue: string | null) {
    if (!newValue) {
        return;
    }

    if (field === 'from') {
        draftFrom.value = newValue;
    } else {
        draftTo.value = newValue;
    }

    draftChoice.value = 'custom';
}

function presetRangeString(months: number): string {
    const to = new Date();
    const from = subMonths(to, months);

    return joinPeriodRangeString(from.toISOString(), to.toISOString());
}

// Presets are a single click, so they commit and close immediately. Custom needs two valid dates
// first, so picking it only reveals the From/To fields - Apply/Cancel below commit or discard those.
function onChoiceSelected(choice: string) {
    draftChoice.value = choice;

    if (choice === 'custom') {
        return;
    }

    modelValue.value = presetRangeString(Number(choice.replace('preset-', '')));
    isOpen.value = false;
}

function onApply() {
    modelValue.value = joinPeriodRangeString(draftFrom.value, draftTo.value);
    isOpen.value = false;
}

function onCancel() {
    isOpen.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Popover), {
    open: isOpen.value,
    "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => ((isOpen).value = $event))
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(PopoverTrigger), { "as-child": "" }, {
        default: _withCtx(() => [
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["h-full flex items-center gap-x-2 px-3 bg-white border rounded-md text-left text-base font-semibold text-dark-gray-600 whitespace-nowrap", isOpen.value ? 'border-black' : 'border-gray-200'])
          }, [
            _createElementVNode("span", null, _toDisplayString(triggerLabel.value), 1),
            _createVNode(_unref(ChevronDown), {
              class: _normalizeClass(["size-5 transform transition-transform", { 'rotate-180': isOpen.value }]),
              "aria-hidden": "true"
            }, null, 8, ["class"])
          ], 2)
        ]),
        _: 1
      }),
      _createVNode(_unref(PopoverContent), {
        align: "end",
        "collision-padding": 16,
        class: "w-auto min-w-[200px] max-w-[calc(100vw-2rem)]"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            ref_key: "popoverBodyEl",
            ref: popoverBodyEl,
            class: "transition-[width] duration-200 ease-out"
          }, [
            _createElementVNode("div", _hoisted_1, _toDisplayString(_ctx.$t('global.uiElements.periodRangeDropdown.sectionLabel')), 1),
            _createVNode(_unref(RadioGroup), {
              "model-value": draftChoice.value,
              class: "flex flex-col gap-y-1",
              "onUpdate:modelValue": onChoiceSelected
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(presetOptions.value, (option) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: option.months,
                    class: _normalizeClass(["flex items-center gap-x-2 rounded-md px-2 py-1.5 -mx-2", { 'bg-primary-300 bg-opacity-20': draftChoice.value === `preset-${option.months}` }])
                  }, [
                    _createVNode(_unref(RadioGroupItem), {
                      id: `period-range-dropdown-preset-${option.months}`,
                      value: `preset-${option.months}`
                    }, null, 8, ["id", "value"]),
                    _createVNode(_unref(Label), {
                      for: `period-range-dropdown-preset-${option.months}`,
                      class: "font-normal cursor-pointer w-full"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(option.label), 1)
                      ]),
                      _: 2
                    }, 1032, ["for"])
                  ], 2))
                }), 128)),
                _createVNode(_unref(Separator), { class: "my-1" }),
                _createElementVNode("div", {
                  class: _normalizeClass(["flex items-center gap-x-2 rounded-md px-2 py-1.5 -mx-2", { 'bg-primary-300 bg-opacity-20': draftChoice.value === 'custom' }])
                }, [
                  _createVNode(_unref(RadioGroupItem), {
                    id: "period-range-dropdown-custom",
                    value: "custom"
                  }),
                  _createVNode(_unref(Label), {
                    for: "period-range-dropdown-custom",
                    class: "font-normal cursor-pointer w-full"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('global.uiElements.periodRangeDropdown.customOptionLabel')), 1)
                    ]),
                    _: 1
                  })
                ], 2)
              ]),
              _: 1
            }, 8, ["model-value"]),
            (draftChoice.value === 'custom')
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createElementVNode("div", {
                    ref_key: "fromFieldEl",
                    ref: fromFieldEl,
                    class: "period-range-dropdown-field"
                  }, [
                    _createVNode(DatePicker, {
                      label: _ctx.$t('global.uiElements.periodRangeDropdown.fromFieldLabel'),
                      required: "",
                      resetable: false,
                      value: draftFrom.value,
                      max: draftTo.value,
                      position: fromCalendarPosition.value,
                      "show-overlay": false,
                      "onUpdate:value": _cache[0] || (_cache[0] = (value) => onFieldChange('from', value))
                    }, null, 8, ["label", "value", "max", "position"])
                  ], 512),
                  _createElementVNode("div", {
                    ref_key: "toFieldEl",
                    ref: toFieldEl,
                    class: "period-range-dropdown-field"
                  }, [
                    _createVNode(DatePicker, {
                      label: _ctx.$t('global.uiElements.periodRangeDropdown.toFieldLabel'),
                      required: "",
                      resetable: false,
                      value: draftTo.value,
                      min: draftFrom.value,
                      position: toCalendarPosition.value,
                      "show-overlay": false,
                      "onUpdate:value": _cache[1] || (_cache[1] = (value) => onFieldChange('to', value))
                    }, null, 8, ["label", "value", "min", "position"])
                  ], 512)
                ]))
              : _createCommentVNode("", true),
            (draftChoice.value === 'custom')
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createVNode(_unref(Button), {
                    variant: "ghost",
                    size: "sm",
                    onClick: onCancel
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('global.uiElements.periodRangeDropdown.cancelButtonLabel')), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(Button), {
                    size: "sm",
                    onClick: onApply
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('global.uiElements.periodRangeDropdown.applyButtonLabel')), 1)
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ], 512)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})